import { useCallback, useEffect, useRef } from 'react';

// Offset to keep the last line of the user message visible when scrolling to assistant message
const USER_MESSAGE_VISIBLE_OFFSET = 48;

type UseSmoothScrollAnchorProps = {
	containerRef: React.RefObject<HTMLDivElement>;
};

const setMinHeight = (
	element: HTMLElement,
	minHeight: number,
	messageElementRef?: React.MutableRefObject<HTMLElement | null>,
) => {
	element.style.setProperty('--smooth-scroll-min-height', `${minHeight}px`);
	element.style.minHeight = `var(--smooth-scroll-min-height)`;
	element.setAttribute('data-smooth-scroll-min-height', 'true');
	if (messageElementRef) {
		messageElementRef.current = element;
	}
};

const removeMinHeight = (
	element: HTMLElement,
	messageElementRef?: React.MutableRefObject<HTMLElement | null>,
) => {
	element.style.removeProperty('--smooth-scroll-min-height');
	element.style.removeProperty('min-height');
	element.removeAttribute('data-smooth-scroll-min-height');
	if (messageElementRef?.current === element) {
		messageElementRef.current = null;
	}
};

export const useSmoothScrollAnchor = ({
	containerRef,
}: UseSmoothScrollAnchorProps): {
	scrollToBottom: ({ smooth }: { smooth: boolean }) => void;
	scrollToLatestMessage: ({ smooth }: { smooth: boolean }) => void;
	maintainScrollHeight: () => void;
} => {
	const messageElementRef = useRef<HTMLElement | null>(null);
	const container = containerRef.current;

	// Automatically remove min-height as content grows naturally
	useEffect(() => {
		if (!container) {
			return;
		}

		const resizeObserver = new ResizeObserver(() => {
			const messageElement = container.querySelector(
				'[data-smooth-scroll-min-height]',
			) as HTMLElement | null;

			if (!messageElement) {
				return;
			}

			const requiredHeight = messageElement.offsetTop + container.clientHeight;

			if (container.scrollHeight >= requiredHeight) {
				removeMinHeight(messageElement, messageElementRef);
			}
		});

		resizeObserver.observe(container);
		return () => resizeObserver.disconnect();
	}, [container]);

	const scrollToBottom = useCallback(
		({ smooth }: { smooth: boolean }): void => {
			requestAnimationFrame(() => {
				if (container) {
					container.scrollTo({
						top: container.scrollHeight,
						behavior: smooth ? 'smooth' : 'instant',
					});
				}
			});
		},
		[container],
	);

	const scrollToLatestMessage = useCallback(
		({ smooth }: { smooth: boolean }): void => {
			requestAnimationFrame(() => {
				if (!container) {
					return;
				}

				const messages = container.querySelectorAll('[data-scroll-anchor="assistant-message"]');
				const assistantMessage = messages[messages.length - 1] as HTMLElement | undefined;
				if (!assistantMessage) {
					return;
				}

				// Calculate scroll position to align element with top of container, offset to show the last line of the user message
				const containerRect = container.getBoundingClientRect();
				const elementRect = assistantMessage.getBoundingClientRect();
				const targetScrollTop =
					container.scrollTop + (elementRect.top - containerRect.top) - USER_MESSAGE_VISIBLE_OFFSET;

				// Calculate required content height and adjust min-height if needed
				const requiredHeight = targetScrollTop + container.clientHeight;
				const currentHeight = container.scrollHeight;

				if (requiredHeight > currentHeight) {
					const additionalSpace = requiredHeight - currentHeight;
					setMinHeight(
						assistantMessage,
						assistantMessage.offsetHeight + additionalSpace,
						messageElementRef,
					);
				} else if (
					messageElementRef.current === assistantMessage &&
					assistantMessage.hasAttribute('data-smooth-scroll-min-height')
				) {
					removeMinHeight(assistantMessage, messageElementRef);
				}

				container.scrollTo({
					top: targetScrollTop,
					behavior: smooth ? 'smooth' : 'instant',
				});
			});
		},
		[container],
	);

	const maintainScrollHeight = useCallback((): void => {
		requestAnimationFrame(() => {
			if (!container) {
				return;
			}

			const messageElement = container.querySelector(
				'[data-smooth-scroll-min-height]',
			) as HTMLElement | null;

			if (!messageElement) {
				return;
			}

			// Calculate the height difference before and after removing min-height on the
			// message element to apply to either the follow up or error message element
			const heightBefore = container.scrollHeight;
			const afterMessageElement = container.querySelector(
				'[data-scroll-anchor="follow-up-message"], [data-scroll-anchor="error-message"]',
			) as HTMLElement | null;

			removeMinHeight(messageElement);

			const heightAfter = container.scrollHeight;
			const heightDifference = heightBefore - heightAfter;

			if (afterMessageElement && heightDifference > 0) {
				const currentHeight = afterMessageElement.offsetHeight;
				setMinHeight(afterMessageElement, currentHeight + heightDifference, messageElementRef);
			} else if (messageElementRef.current === messageElement) {
				messageElementRef.current = afterMessageElement || null;
			}
		});
	}, [container]);

	return { scrollToBottom, scrollToLatestMessage, maintainScrollHeight };
};
